import { PERMIT2_ADDRESS } from "@uniswap/permit2-sdk";
import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";

import { SupCheckCircle, SupX, SupXCircle } from "@repo/core-ui";
import { SupButton, SupLoader, SupText } from "@repo/core-ui";

import { MaxUint256 } from "@/lib/constants";
import { SupWeb3Balance } from "@/types";
import { checkIfRejected, classNames, depositTransferError, getWeb3Address, notifyMessage } from "@/utils";
import { useWriteContract, useWaitForTransactionReceipt, useSimulateContract } from "wagmi";
import { getERC20Abi } from "@/abis/erc20";

interface SupWeb3DepositStepApprovalApprovePermit2Props {
  selectedBalance: SupWeb3Balance;
  setPauseRequests: Dispatch<SetStateAction<boolean>>;
  setIsUniswapContractApproved: Dispatch<SetStateAction<boolean>>;
  disabled?: boolean;
}

export default function Web3DepositApprovalApprovePermit2({
  selectedBalance,
  setIsUniswapContractApproved,
  setPauseRequests,
  disabled = false,
}: SupWeb3DepositStepApprovalApprovePermit2Props) {
  const defaultButtonText = `Approve Permit2 contract for ${selectedBalance.symbol}`;
  const toastId = `permit2_${selectedBalance.address}`;

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | undefined | null>(undefined);
  const [buttonText, setButtonText] = useState<string>(defaultButtonText);

  const {
    data: config,
    error: prepareError,
    isLoading: isPrepareLoading,
  } = useSimulateContract({
    abi: getERC20Abi(getWeb3Address(selectedBalance.address), selectedBalance.chainId),
    chainId: selectedBalance.chainId as number,
    address: getWeb3Address(selectedBalance.address),
    functionName: "approve",
    args: [PERMIT2_ADDRESS, MaxUint256.toBigInt()],
  });

  const { data, error: sendError, isPending: isSendLoading, writeContract } = useWriteContract();

  const {
    isLoading: isWaitLoading,
    isSuccess: isWaitSuccess,
    error: waitError,
  } = useWaitForTransactionReceipt({
    hash: data,
  });

  // Setting the loading state
  useEffect(() => {
    if (isPrepareLoading) {
      setButtonText("Preparing tx...");
    }
    if (isSendLoading) {
      setButtonText("Signing tx... (check the wallet)");
    }
    if (isWaitLoading) {
      setButtonText("Processing tx...");
    }
    if (error) {
      checkIfRejected(error) ? setButtonText(defaultButtonText) : setButtonText("Can't do approval");
    } else {
      if (!isLoading) {
        setButtonText(defaultButtonText);
      }
    }
  }, [isPrepareLoading, isSendLoading, isWaitLoading, error, isLoading, defaultButtonText]);

  useEffect(() => {
    setIsLoading(isPrepareLoading || isSendLoading || isWaitLoading);
  }, [isWaitLoading, isSendLoading, isPrepareLoading]);

  useEffect(() => {
    if (isWaitSuccess) {
      setIsLoading(false);
      setPauseRequests(false);
      setIsUniswapContractApproved(true);
      notifyMessage("Permit2 Approval", `Permit2 now has access to your ${selectedBalance.symbol} on ${selectedBalance.chainName}`, "success", <SupCheckCircle />, toastId);
    }
  }, [isWaitSuccess, setIsUniswapContractApproved, selectedBalance.chainName, selectedBalance.symbol, toastId, setPauseRequests]);

  useEffect(() => {
    if (error) {
      if (checkIfRejected(error)) {
        notifyMessage("Permit2 Approval", `Transaction rejected`, "warning", <SupXCircle />, toastId);
        setError(undefined);
      } else {
        notifyMessage("Permit2 Approval error", depositTransferError(error), "error", <SupXCircle />, toastId, 0);
      }
      setIsLoading(false);
      setPauseRequests(false);
    }
  }, [error, setPauseRequests, toastId]);

  useEffect(() => {
    setError(prepareError || sendError || waitError);
  }, [waitError, sendError, prepareError]);

  useEffect(() => {
    if (data) {
      notifyMessage("Permit2 Approval", `Processing TX #${data}`, "primary", <SupLoader />, toastId, 4000);
    }
  }, [data, toastId, selectedBalance.address]);

  const callApprove = useCallback(() => {
    try {
      setPauseRequests(true);
      setIsLoading(true);
      config?.request && writeContract?.(config.request);
    } catch (e) {
      notifyMessage("Permit2 Approval error", depositTransferError(e as Error), "error", <SupXCircle />, toastId, 0);
      setIsLoading(false);
      setPauseRequests(false);
    }
  }, [config?.request, setPauseRequests, toastId, writeContract]);

  const prefixIcon = isLoading ? <SupLoader isSmall color="primary" className="text-neutral-700" svgClassName="stroke-2" /> : !config?.request ? <SupX /> : undefined;

  /**
   * Web3 transaction awaiting
   */
  return (
    <div className="h-fit max-h-[220px]">
      <SupButton
        fluid
        prefixIcon={prefixIcon}
        disabled={!writeContract || isLoading || disabled || !!error}
        className={classNames("w-full justify-center", (!config?.request || isLoading || disabled) && "cursor-not-allowed")}
        color={!config?.request && !isLoading ? "error" : "primary"}
        onClick={callApprove}
      >
        {buttonText}
      </SupButton>
      {!disabled && (
        <SupText variant="caption" family="mono" className="mt-2 whitespace-break-spaces bg-primary-50 p-1">
          Before signing the approval for Superform contract through Permit2, you must approve the Permit2 contract through the {selectedBalance.symbol} token contract. Learn more
          at&nbsp;
          <a href="https://docs.uniswap.org/contracts/permit2/overview" target="_blank" className="underline hover:no-underline">
            Uniswap documentation site
          </a>
          .
        </SupText>
      )}
    </div>
  );
}
